import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users, 
  Calendar, 
  Settings, 
  Package, 
  Ticket, 
  FileText, 
  BarChart, 
  Bell, 
  HelpCircle, 
  LogOut,
  X,
  Clock
} from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';
import Logo from './Logo';

interface AdminSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function AdminSidebar({ isOpen, onClose }: AdminSidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useAdminAuth();

  const menuItems = [
    {
      section: 'Main',
      items: [
        { path: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
        { path: '/admin/users', icon: Users, label: 'Users' },
        { path: '/admin/bookings', icon: Calendar, label: 'Bookings' },
        { path: '/admin/services', icon: Package, label: 'Services' },
        { path: '/admin/time-slots', icon: Clock, label: 'Time Slots' },
      ]
    },
    {
      section: 'Marketing',
      items: [
        { path: '/admin/coupons', icon: Ticket, label: 'Coupons' },
        { path: '/admin/reviews', icon: FileText, label: 'Reviews' },
      ]
    },
    {
      section: 'System',
      items: [
        { path: '/admin/analytics', icon: BarChart, label: 'Analytics' },
        { path: '/admin/notifications', icon: Bell, label: 'Notifications' },
        { path: '/admin/settings', icon: Settings, label: 'Settings' },
        { path: '/admin/audit-logs', icon: FileText, label: 'Audit Logs' },
      ]
    }
  ];

  const isActive = (path: string) => location.pathname === path;

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/admin/login');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-screen w-72 bg-white dark:bg-gray-800 border-r dark:border-gray-700 overflow-y-auto z-50 transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } md:translate-x-0`}
      >
        {/* Mobile Close Button */}
        <div className="md:hidden flex items-center justify-between p-4 border-b dark:border-gray-700">
          <Logo size="small" />
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            <X className="w-6 h-6 text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Desktop Header */}
        <div className="hidden md:flex items-center p-6">
          <Logo size="small" linkTo="/admin" />
        </div>

        <nav className="p-6 space-y-8">
          {menuItems.map((section, index) => (
            <div key={index}>
              <h3 className="px-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                {section.section}
              </h3>
              <div className="space-y-1">
                {section.items.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={onClose}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 group ${
                      isActive(item.path)
                        ? 'bg-blue-50 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    <div className={`transition-transform duration-200 group-hover:scale-110 ${
                      isActive(item.path) ? 'transform scale-110' : ''
                    }`}>
                      <item.icon className="w-5 h-5" />
                    </div>
                    <span>{item.label}</span>
                    {isActive(item.path) && (
                      <div className="ml-auto w-1.5 h-1.5 rounded-full bg-blue-600 dark:bg-blue-400" />
                    )}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-6 border-t dark:border-gray-700 bg-white dark:bg-gray-800">
          <div className="flex flex-col gap-4">
            <Link
              to="/admin/help"
              onClick={onClose}
              className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
            >
              <HelpCircle className="w-5 h-5" />
              <span>Help & Documentation</span>
            </Link>
            <button
              onClick={handleSignOut}
              className="flex items-center gap-2 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
            >
              <LogOut className="w-5 h-5" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}